// src/components/forms/FormRenderer.tsx
// ==== Utworzony w Krok 3

import React from 'react';
import { Box, Button, Alert, CircularProgress } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { updateFormField, setValidationError, clearValidationErrors, submitForm } from '../../store/slices/formsSlice';
import { FormSection } from '../../types/forms';
import { NumberField, SelectField, TemplateField, TextField, SelectManyField } from './fields';
import { evaluateVisibility } from '../../utils/visibilityEngine';

// ==== Dodane w Krok 4A >>>>
import {  Snackbar } from '@mui/material';
import { clearSubmitState } from '../../store/slices/formsSlice';
import { DatePickerField, DateRangePickerField } from './fields';
import { NodePickerField } from './fields';
// <<<< Dodane w Krok 4A ====


interface FormRendererProps {
  formTemplateId: number;
   // ==== Dodane w Krok 4A >>>>
  nodeId: number;
  // <<<< Dodane w Krok 4A ====

}

// ==== Zmiana w Krok 4A >>>>
// ==== z:
//export const FormRenderer: React.FC<FormRendererProps> = ({ formTemplateId }) => {
// ==== na:
export const FormRenderer: React.FC<FormRendererProps> = ({ formTemplateId, nodeId }) => {
// <<<< zmiana w Krok 4A ====
  const dispatch = useAppDispatch();
  const { 
    currentForm, 
    formData, 
    validationErrors, 
    isSubmitting, 
    submitSuccess, 
    error 
  } = useAppSelector((state: any) => state.forms);

  const handleFieldChange = (sectionId: string, value: any) => {
    dispatch(updateFormField({ sectionId, value }));
  };

  const validateForm = (): boolean => {
    if (!currentForm) return false;

    let isValid = true;
    dispatch(clearValidationErrors());

    currentForm.sections.forEach((section: any) => {
      // Skip template sections
      if (section.type === 'template') return;
      
      // Skip hidden sections
      const isVisible = evaluateVisibility(section.visibility, formData);
      if (!isVisible) return;
      
      // Check required validation
      if (section.validation?.required) {
        const value = formData[section.id];
        if (value === undefined || value === null || value === '' || 
            (Array.isArray(value) && value.length === 0)) {
          dispatch(setValidationError({ 
            sectionId: section.id, 
            error: 'To pole jest wymagane' 
          }));
          isValid = false;
        }
      }
    });

    return isValid;
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!validateForm()) return;
   
    // =============================================================================================
    // ==== Zmiana w Krok 4B >>>>
    // ==== z:
    //dispatch(submitForm({ formTemplateId, formData }));
    // ==== na:
    // Przygotuj responses object
    const responses: Record<string, any> = {};
    if (currentForm) {
      currentForm.sections.forEach((section: FormSection) => {
        const value = formData[section.id];
        if (value !== undefined && value !== null && value !== '') {
          responses[section.id] = {
            id: section.id,
            type: section.type,
            value: value
          };
        }
      });
    }

    // Submit z wszystkimi wymaganymi parametrami
    dispatch(submitForm({ 
      nodeId, // To musisz mieć jako prop lub z Redux state
      formTemplateId, 
      formData,
      responses 
    }));

    // <<<< Zmiana w Krok 4B ====
    // =============================================================================================
    
  };

  const renderField = (section: FormSection) => {
    const value = formData[section.id];
    const error = validationErrors[section.id];

    // Evaluate visibility using our engine
    const isVisible = evaluateVisibility(section.visibility, formData);
    if (!isVisible) {
      return null;
    }

    switch (section.type) {
      case 'template':
        return <TemplateField key={section.id} section={section} />;
        
      case 'number':
        return (
          <NumberField
            key={section.id}
            section={section}
            value={value}
            onChange={(newValue) => handleFieldChange(section.id, newValue)}
            error={error}
          />
        );
        
      case 'text':
        return (
          <TextField
            key={section.id}
            section={section}
            value={value}
            onChange={(newValue) => handleFieldChange(section.id, newValue)}
            error={error}
          />
        );
        
      case 'select-one':
        return (
          <SelectField
            key={section.id}
            section={section}
            value={value}
            onChange={(newValue) => handleFieldChange(section.id, newValue)}
            error={error}
          />
        );
        
      case 'select-many':
        return (
          <SelectManyField
            key={section.id}
            section={section}
            value={value}
            onChange={(newValue) => handleFieldChange(section.id, newValue)}
            error={error}
          />
        );

      // =============================================================================================
      // ==== Dodane w Krok 4A >>>>

      case 'date-picker':
        return (
          <DatePickerField
            key={section.id}
            section={section}
            value={value}
            onChange={(newValue) => handleFieldChange(section.id, newValue)}
            error={error}
          />
        );

      case 'date-range-picker':
        return (
          <DateRangePickerField
            key={section.id}
            section={section}
            value={value}
            onChange={(newValue) => handleFieldChange(section.id, newValue)}
            error={error}
          />
        );

      case 'node-picker':
        return (
          <NodePickerField
            key={section.id}
            section={section}
            value={value}
            onChange={(newValue) => handleFieldChange(section.id, newValue)}
            error={error}
          />
        );

      // <<<< Dodane w Krok 4A ====
      // =============================================================================================
        
      default:
        return (
          <Box key={section.id} sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <Alert severity="info">
              Nieobsługiwany typ pola: <strong>{section.type}</strong> (ID: {section.id})
              <br />
              <em>{section.label}</em>
            </Alert>
          </Box>
        );
    }
  };

  if (!currentForm) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      {/* Form Header */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Box 
          sx={{ 
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 64,
            height: 64,
            borderRadius: '50%',
            backgroundColor: currentForm.symbolBackColor || '#BCAAA4',
            color: currentForm.symbolTextColor || 'white',
            fontSize: '24px',
            fontWeight: 'bold',
            mb: 2
          }}
        >
          {currentForm.symbol}
        </Box>
        <h2>{currentForm.name}</h2>
        <p>{currentForm.description}</p>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Success Alert */}
      {submitSuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Formularz został pomyślnie wysłany!
        </Alert>
      )}

      {/* Form Sections */}
      {currentForm.sections.map(renderField)}

      {/* Submit Button */}
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Button
          type="submit"
          variant="contained"
          size="large"
          disabled={isSubmitting || submitSuccess}
          sx={{ minWidth: 200 }}
        >
          {isSubmitting ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              Wysyłanie...
            </>
          ) : (
            'Wyślij formularz'
          )}
        </Button>
      </Box>
    </Box>
  );
};